import React, { useCallback, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { ContactFormFields } from '@customTypes/ContactFormFields'
import ContactScreen from '@screens/Contact'
import useWizardManager from '@hooks/useWizardManager'
import { useConfig } from '@context/ConfigurationContext'
import {
  useContactDetailsState,
  useContactDetailsDispatch,
  setContactDetailsFields,
} from '@context/ContactDetailsContext'
import { useChoiceState } from '@context/ChoiceContext'
import routes from 'src/config/routes'
import validateSelectedAppointment from 'src/utils/validateSelectedAppointment'
import { seenContactForm } from 'src/services/analytics'
import { useGetTreatmentInfo } from '@hooks/useGetTreatmentInfo'
import { PatientTypeOptions } from 'src/config/patientTypeOptions'
import { useCreateBookingForNewPatient } from '@hooks/useCreateBookingForNewPatient'
import { usePrepareBooking } from '@hooks/usePrepareBooking'
import { useMutation } from 'react-query'
import { verifyPhoneNumberForNewPatient } from 'src/services/api/verifyPhone'
import {
  addVerifyInfo,
  canRevalidate,
  initialSmsSent,
  setInitialSmsSent,
  useVerifyPhoneContext,
  useVerifyPhoneDispatch,
} from '@context/VerifyPhoneContext'
import { getErrorTranslationKey } from 'src/helpers/getApiErrorTranslationKey'

const Contact = () => {
  useWizardManager({
    targetRoute: routes.contact,
    onValidStep: () => seenContactForm(),
  })
  const config = useConfig()
  const contactDetailsDispatch = useContactDetailsDispatch()
  const history = useHistory()
  const contactDetails = useContactDetailsState()
  const choices = useChoiceState()
  const [error, setError] = useState('')
  const [initialValues] = useState(contactDetails)
  const treatmentInfo = useGetTreatmentInfo()
  const verifyPhoneDispatch = useVerifyPhoneDispatch()
  const shouldVerifyPhoneNumber = choices.patientType === PatientTypeOptions.NEW || !config.isInternalClinic
  const prepareBooking = usePrepareBooking()
  const { verifyInfo } = useVerifyPhoneContext()

  const handleBackClicked = () => {
    history.push(routes.appointment)
  }

  const { isLoading: isProcessing, mutate: createBooking } = useCreateBookingForNewPatient((errorCode) => {
    setError(errorCode)
  })
  const { mutate: verifyNumber } = useMutation(verifyPhoneNumberForNewPatient, {
    onSuccess: (data) => {
      verifyPhoneDispatch(addVerifyInfo(data.phone || '', data.validity))
      history.push(routes.phoneVerification)
    },
    onError: (error) => {
      setError?.(getErrorTranslationKey(error))
    },
  })

  const handleNextClicked = useCallback(
    (values: ContactFormFields, token?: string) => {
      const isValidAppointment = validateSelectedAppointment(choices.selectedBookableDateAndTime)

      if (!isValidAppointment) {
        history.push(routes.appointment)
        return
      }

      const booking = prepareBooking(values, token)

      if (
        shouldVerifyPhoneNumber &&
        canRevalidate(verifyInfo, values.phone) &&
        !initialSmsSent(verifyInfo, values.phone)
      ) {
        verifyPhoneDispatch(setInitialSmsSent(values.phone))
        const booking = prepareBooking(values)
        verifyNumber(booking)
      } else if (shouldVerifyPhoneNumber) {
        history.push(routes.phoneVerification)
      } else {
        createBooking(booking)
      }
    },
    [
      choices,
      createBooking,
      history,
      shouldVerifyPhoneNumber,
      prepareBooking,
      verifyNumber,
      verifyPhoneDispatch,
      verifyInfo,
    ]
  )

  const handleFormChange = useCallback(
    async (values: ContactFormFields) => {
      contactDetailsDispatch(setContactDetailsFields(values))
    },
    [contactDetailsDispatch]
  )

  return (
    <ContactScreen
      treatmentInfo={treatmentInfo}
      initialValues={initialValues}
      onBackClicked={handleBackClicked}
      onNextClicked={handleNextClicked}
      onFormChange={handleFormChange}
      isProcessing={isProcessing}
      error={error}
      nonModalEmbed={config.nonModalEmbed}
      shouldVerifyPhoneNumber={shouldVerifyPhoneNumber}
    />
  )
}

export default Contact
